// Purpose: let a Console operator place an outbound test call for the agent they are
// editing, choosing which tenant DID to call from, without a CLI or runtime token.
import { PhoneCall } from "lucide-react";
import { useEffect, useState } from "react";
import type { FormEvent } from "react";
import {
  ConsoleApiError,
  createTestCall,
  fetchContactMemoryPreview,
  fetchNumbers,
  isUnauthorizedApiError,
  linkContactPhone,
} from "./apiClient";
import type { ContactMemoryPreview } from "./apiClient";
import type { ConsoleNumber } from "./mockData";

interface TestCallPanelProps {
  agentId: string | null;
  agentName: string | null;
  hasPublishedVersion: boolean;
  onSessionExpired: () => void;
}

const E164 = /^\+[1-9][0-9]{7,14}$/;

function normalizeAustralianPhoneNumber(value: string): string {
  const compact = value.trim().replace(/[\s()-]/g, "");
  if (/^04\d{8}$/.test(compact)) return `+61${compact.slice(1)}`;
  if (/^61\d{9}$/.test(compact)) return `+${compact}`;
  return compact;
}

/** State the memory outcome plainly, so an operator never dials to find out. */
function memoryHeadline(preview: ContactMemoryPreview | null): string {
  if (!preview)
    return "Memory: enter a contact ID or destination number to check.";
  if (!preview.memory_available)
    return `Memory: none. ${preview.status_reason}.`;
  const revision = preview.revision_number
    ? ` (revision ${preview.revision_number})`
    : "";
  const source =
    preview.resolved_from === "phone" ? " resolved from this number" : "";
  return `Memory: will load for ${preview.contact_id}${source}${revision}.`;
}

export function TestCallPanel(props: TestCallPanelProps) {
  const { agentId, agentName, hasPublishedVersion, onSessionExpired } = props;
  const [numbers, setNumbers] = useState<ConsoleNumber[]>([]);
  const [selectedFromNumber, setSelectedFromNumber] = useState<string | null>(
    null,
  );
  const [toNumber, setToNumber] = useState("");
  const [contactId, setContactId] = useState("");
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [startedCallId, setStartedCallId] = useState<string | null>(null);
  const [isDialing, setIsDialing] = useState(false);
  const [preview, setPreview] = useState<ContactMemoryPreview | null>(null);
  const [pendingRelink, setPendingRelink] = useState<{ contactId: string; phone: string } | null>(null);

  useEffect(() => {
    let cancelled = false;
    fetchNumbers()
      .then((loaded) => {
        if (!cancelled) setNumbers(loaded);
      })
      .catch((error) => {
        if (isUnauthorizedApiError(error)) onSessionExpired();
      });
    return () => {
      cancelled = true;
    };
  }, [onSessionExpired]);

  // Answer "will this call have memory?" before dialing, so a silent no-op is impossible.
  const normalizedToNumber = normalizeAustralianPhoneNumber(toNumber);
  const destinationIsValid = E164.test(normalizedToNumber);
  const trimmedContactId = contactId.trim();
  const hasSelector = Boolean(trimmedContactId) || destinationIsValid;
  useEffect(() => {
    if (!hasSelector) return;
    let cancelled = false;
    const timer = setTimeout(() => {
      fetchContactMemoryPreview({
        contactId: trimmedContactId || undefined,
        phone: destinationIsValid ? normalizedToNumber : undefined,
      })
        .then((result) => {
          if (!cancelled) setPreview(result);
        })
        .catch((error) => {
          if (isUnauthorizedApiError(error)) onSessionExpired();
          if (!cancelled) setPreview(null);
        });
    }, 400);
    return () => {
      cancelled = true;
      clearTimeout(timer);
    };
  }, [
    trimmedContactId,
    normalizedToNumber,
    destinationIsValid,
    hasSelector,
    onSessionExpired,
  ]);

  // Derived, so clearing the inputs cannot leave a stale memory claim on screen.
  const activePreview = hasSelector ? preview : null;

  async function linkNumberToContact(options: { allowRelink?: boolean } = {}) {
    if (!trimmedContactId || !destinationIsValid) return;
    try {
      if (options.allowRelink) {
        await linkContactPhone(trimmedContactId, normalizedToNumber, { allowRelink: true });
      } else {
        await linkContactPhone(trimmedContactId, normalizedToNumber);
      }
      const refreshed = await fetchContactMemoryPreview({ phone: normalizedToNumber });
      setPreview(refreshed);
      setPendingRelink(null);
      setStatusMessage(`${normalizedToNumber} now resolves to ${trimmedContactId} on future calls.`);
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      if (error instanceof ConsoleApiError && error.status === 409) {
        setPendingRelink({ contactId: trimmedContactId, phone: normalizedToNumber });
        setStatusMessage(`${normalizedToNumber} is already linked to another contact.`);
        return;
      }
      setStatusMessage(
        error instanceof ConsoleApiError ? error.message : "Could not link this number.",
      );
    }
  }

  const outboundNumbers = numbers.filter(
    (number) => number.state === "Live" || number.state === "Propagating",
  );
  const defaultFromNumber = outboundNumbers[0]
    ? (outboundNumbers[0].rawDid ?? outboundNumbers[0].did)
    : "";
  // Derive rather than store, so switching agents cannot leave a stale DID selected.
  const fromNumber = outboundNumbers.some(
    (number) => (number.rawDid ?? number.did) === selectedFromNumber,
  )
    ? (selectedFromNumber as string)
    : defaultFromNumber;

  const blockedReason = !agentId
    ? "Select a live agent before placing a test call."
    : !hasPublishedVersion
      ? "Publish a version before this agent can take a call."
      : outboundNumbers.length === 0
        ? "No outbound-capable DID is available. Add or activate a number on the Numbers screen first."
        : null;
  const canDial = !blockedReason && Boolean(fromNumber) && destinationIsValid;
  const canLinkNumber =
    Boolean(trimmedContactId) &&
    destinationIsValid &&
    activePreview?.resolved_from !== "phone";
  const canConfirmRelink =
    pendingRelink?.contactId === trimmedContactId && pendingRelink.phone === normalizedToNumber;

  async function startTestCall(event: FormEvent) {
    event.preventDefault();
    if (!canDial || !agentId) return;
    setIsDialing(true);
    setStatusMessage(null);
    setStartedCallId(null);
    try {
      const result = await createTestCall({
        agent_id: agentId,
        from_number: fromNumber,
        to_number: normalizedToNumber,
        contact_id: trimmedContactId || null,
      });
      setStartedCallId(result.console_call_id);
      setStatusMessage(`Calling ${normalizedToNumber} now.`);
    } catch (error) {
      if (isUnauthorizedApiError(error)) {
        onSessionExpired();
        return;
      }
      setStatusMessage(
        error instanceof ConsoleApiError
          ? error.message
          : "Could not start the test call.",
      );
    } finally {
      setIsDialing(false);
    }
  }

  return (
    <section
      className="panel builder-panel"
      aria-labelledby="test-call-heading"
    >
      <div className="panel-title-row">
        <h2 id="test-call-heading">
          <PhoneCall size={16} aria-hidden="true" /> Test call
        </h2>
        <span className="table-label">Outbound UAT</span>
      </div>
      <p className="compact-copy">
        Call yourself with the published version of {agentName ?? "this agent"}{" "}
        to hear it live. Any active tenant DID can be used for outbound testing;
        inbound routing still belongs to the single assigned agent on the
        Numbers screen.
      </p>
      {blockedReason ? (
        <p className="compact-copy muted-row">{blockedReason}</p>
      ) : null}
      <form
        className="stacked-form"
        onSubmit={(event) => void startTestCall(event)}
      >
        <label>
          Call from
          <select
            value={fromNumber}
            onChange={(event) => setSelectedFromNumber(event.target.value)}
            disabled={Boolean(blockedReason)}
          >
            {outboundNumbers.length === 0 ? (
              <option value="">No outbound-capable DID available</option>
            ) : null}
            {outboundNumbers.map((number) => {
              const value = number.rawDid ?? number.did;
              const inboundOwner =
                number.agent && number.agent !== "—"
                  ? ` · inbound: ${number.agent}`
                  : " · no inbound assignment";
              return (
                <option key={value} value={value}>
                  {number.did}
                  {number.uatRole === "staging_test_did"
                    ? " (staging test DID)"
                    : ""}
                  {inboundOwner}
                </option>
              );
            })}
          </select>
        </label>
        <label>
          Call to
          <input
            required
            value={toNumber}
            onChange={(event) => setToNumber(event.target.value)}
            placeholder="+61400000000 or 0400 000 000"
            className="mono"
            disabled={Boolean(blockedReason)}
          />
          {toNumber.trim() && normalizedToNumber !== toNumber.trim() ? (
            <span className="field-help">Will dial {normalizedToNumber}</span>
          ) : null}
        </label>
        <label>
          Contact ID
          <input
            value={contactId}
            onChange={(event) => setContactId(event.target.value)}
            placeholder="Optional: resolved from the number when linked"
            className="mono"
            disabled={Boolean(blockedReason)}
          />
        </label>
        <div className="notice compact-notice" role="status">
          <strong>{memoryHeadline(activePreview)}</strong>
          {activePreview?.agent_visible_summary ? (
            <p>{activePreview.agent_visible_summary}</p>
          ) : null}
          {activePreview?.last_interaction_at ? (
            <p className="compact-copy">
              Last interaction{" "}
              {new Date(activePreview.last_interaction_at).toLocaleString(
                "en-AU",
              )}
            </p>
          ) : null}
          {canLinkNumber && !canConfirmRelink ? (
            <button
              className="secondary-button compact-action"
              type="button"
              onClick={() => void linkNumberToContact()}
            >
              Link {normalizedToNumber} to {trimmedContactId}
            </button>
          ) : null}
          {canConfirmRelink ? (
            <button
              className="secondary-button compact-action"
              type="button"
              onClick={() => void linkNumberToContact({ allowRelink: true })}
            >
              Move this number to {trimmedContactId}
            </button>
          ) : null}
        </div>
        <button
          className="primary-button"
          type="submit"
          disabled={isDialing || !canDial}
        >
          <PhoneCall size={15} aria-hidden="true" />
          {isDialing ? "Dialing…" : "Start test call"}
        </button>
      </form>
      {statusMessage ? (
        <p className="compact-copy" role="status">
          {statusMessage}
        </p>
      ) : null}
      {startedCallId ? (
        <p className="compact-copy">
          Open Calls and select call{" "}
          <span className="mono">{startedCallId}</span> to review transcript,
          cost, and memory impact.
        </p>
      ) : null}
    </section>
  );
}
